/* eslint-disable max-lines */
/* eslint-disable max-statements */

/* eslint-disable max-lines-per-function */
import prepareComponent from "@bluframe/grapple/prepareComponent"
import {
  ImageSelectorImage,
  ImageSelectorProps
} from "@bluframe/mightychroma-mightytool-frontend-types"
import getImageIdentifier from "helpers/images/getImageIdentifier"
import extractFQDN from "helpers/urls/extractFQDN"
import {
  createRef,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"

import ImageSelector from "./ImageSelector"
import { ImageAndIdentifier } from "./Item"

interface Props extends ImageSelectorProps {
  showScreenshotMetadataInputs?: boolean
}

export interface ComponentProps extends Omit<
  Props,
  | "images"
  | "onAddImage"
  | "onConfirm"
  | "onImageUrlChange"
  | "onInferImageCredit"
  | "onRegenerateScreenshotMetadataField"
  | "onSelectFrontImage"
  | "onSetImageAlternativeText"
  | "onSetImageCaption"
  | "onSetImageCredit"
  | "onUpdateSelectedImagePosition"
> {
  imageUrlRefs: RefObject<HTMLInputElement | null>[]
  imagesAndIdentifiers: ImageAndIdentifier[]
  onAddImage: () => void
  onConfirm: () => void
  onSelectImage: (image: ImageSelectorImage) => () => void
  onImageUrlChange: (
    identifier: string,

    url: string
  ) => void
  onSetImageCredit: (identifier: string, credit: string) => void
  onSetImageAlternativeText: (
    identifier: string,
    alternativeText: string
  ) => void
  onSetImageCaption: (identifier: string, caption: string) => void
  onRegenerateScreenshotMetadataField?: (input: {
    contentId?: number | string
    field: "alternativeText" | "caption"
    id?: string
    originalUrl: string
  }) => Promise<string | undefined>
  onUpdateSelectedImagePosition: (
    direction: "left" | "right"
  ) => (identifier: string) => void
  mediaLabel?: string
}

export const usePrepareComponent = ({
  isMultiSelect,
  onAddImage: onAddImageProps,
  onConfirm: onConfirmProps,
  onInferImageCredit,
  onRegenerateScreenshotMetadataField,
  modal,
  ...props
}: Props): ComponentProps => {
  const [images, setImages] = useState<Props["images"]>(() => props.images)
  const [imageUrlRefs, setImageUrlRefs] = useState<
    RefObject<HTMLInputElement | null>[]
  >([])
  const [pendingFocusIndex, setPendingFocusIndex] = useState<number | null>(
    null
  )
  const inferredImageCreditsRef = useRef(new Set<string>())

  const prevLength = useRef(images.length)

  useEffect(() => {
    if (images.length > prevLength.current) {
      const lastRef = imageUrlRefs[imageUrlRefs.length - 1]?.current
      lastRef?.focus()
    }
    prevLength.current = images.length
  }, [images.length, imageUrlRefs])

  /**
   * imagesAndIdentifiers
   *
   * We use this to create a unique identifier for each image
   */
  const imagesAndIdentifiers = useMemo(
    () =>
      images.map((image) => ({
        ...image,
        identifier: getImageIdentifier(image)
      })),
    [images]
  )

  /**
   * selectedImages
   *
   *  Selected images
   */
  const selectedImages = useMemo(
    () =>
      images.filter((image) => {
        return image.isSelected
      }),
    [images]
  )

  /**
   * Every time the images change from props we need to update the state
   */
  useEffect(() => {
    let cancelled = false
    queueMicrotask(() => {
      if (!cancelled) {
        setImages(props.images)
      }
    })
    return () => {
      cancelled = true
    }
  }, [props.images])

  /**
   * Sync imageUrlRefs array with images array length
   */
  useEffect(() => {
    let cancelled = false

    queueMicrotask(() => {
      if (cancelled) return

      setImageUrlRefs((prevRefs) => {
        const newRefs = [...prevRefs]

        // Remove excess refs
        if (newRefs.length > images.length) {
          newRefs.splice(images.length)
        }

        // Add new refs
        for (let index = newRefs.length; index < images.length; index += 1) {
          newRefs.push(createRef<HTMLInputElement>())
        }

        return newRefs
      })
    })

    return () => {
      cancelled = true
    }
  }, [images])

  useEffect(() => {
    if (
      pendingFocusIndex !== null &&
      imageUrlRefs[pendingFocusIndex] &&
      imageUrlRefs[pendingFocusIndex].current
    ) {
      imageUrlRefs[pendingFocusIndex].current?.focus()
      queueMicrotask(() => {
        setPendingFocusIndex(null)
      })
    }
  }, [imageUrlRefs, pendingFocusIndex])

  /**
   * inferImageCredit
   *
   * @description
   * Infers an image credit from the URL when the consumer exposes
   * an inference callback.
   */
  const inferImageCredit = useCallback(
    (identifier: string, url: string) => {
      const normalizedUrl = url.trim()

      if (!onInferImageCredit || !normalizedUrl) {
        return
      }

      const fqdn = extractFQDN(normalizedUrl)

      if (!fqdn) {
        return
      }

      const lookupKey = `${identifier}:${normalizedUrl}`

      if (inferredImageCreditsRef.current.has(lookupKey)) {
        return
      }

      inferredImageCreditsRef.current.add(lookupKey)

      onInferImageCredit(fqdn)
        .then((credit) => {
          const normalizedCredit = credit?.trim()

          if (!normalizedCredit) {
            return
          }

          setImages((prevState) =>
            prevState.map((item) => {
              const itemIdentifier = getImageIdentifier(item)
              const isMatchingImage =
                identifier === itemIdentifier ||
                item.originalUrl === normalizedUrl

              return isMatchingImage && !item.credit
                ? {
                    ...item,
                    credit: normalizedCredit
                  }
                : item
            })
          )
        })
        .catch(() => undefined)
    },
    [onInferImageCredit]
  )

  useEffect(() => {
    images.forEach((image) => {
      if (!image.originalUrl || image.credit) {
        return
      }

      inferImageCredit(getImageIdentifier(image), image.originalUrl)
    })
  }, [images, inferImageCredit])

  /**
   * onAddImage
   *
   * @description Add image to the selected images
   * @returns
   *
   */
  const onAddImage = useCallback(() => {
    const image: ImageSelectorImage = { originalUrl: "" }
    setImages((prevImages) => [...prevImages, image])
    setPendingFocusIndex(images.length)
    onAddImageProps?.(image)
  }, [onAddImageProps, images.length])

  /**
   * onConfirm
   *
   * @description Confirm selected image and URL and close modal
   * Return url only if the selected service is url
   * @returns
   *
   */
  const onConfirm = useCallback(() => {
    onConfirmProps(selectedImages)

    modal.onClose()
  }, [modal, selectedImages, onConfirmProps])

  /**
   * onSelectImage
   *
   * @description Select the image service
   * @param {ImageSelectorImage} image
   * @returns
   *
   */
  const onSelectImage = useCallback(
    (image: ImageSelectorImage) => () => {
      setImages((prevImages) => {
        const imageIdentifier = getImageIdentifier(image)

        if (isMultiSelect) {
          return prevImages.map((img) => {
            const imgIdentifier = getImageIdentifier(img)

            return imgIdentifier === imageIdentifier
              ? { ...img, isSelected: !img.isSelected }
              : img
          })
        }

        return prevImages.map((img) => {
          const imgIdentifier = getImageIdentifier(img)

          return imgIdentifier === imageIdentifier
            ? { ...img, isSelected: true }
            : { ...img, isSelected: false }
        })
      })
    },
    [isMultiSelect]
  )

  /**
   * onImageUrlChange
   *
   * @description
   * Updates the image URL as the user types/pastes and triggers
   * credit inference when available.
   */
  const onImageUrlChange = useCallback(
    (identifier: string, url: string) => {
      setImages((prevState) =>
        prevState.map((item) => {
          const itemIdentifier = getImageIdentifier(item)

          return identifier === itemIdentifier
            ? {
                ...item,
                originalUrl: url
              }
            : item
        })
      )

      inferImageCredit(identifier, url)
    },
    [inferImageCredit]
  )

  const onSetImageCredit = useCallback((identifier: string, credit: string) => {
    const normalizedCredit = credit.trim()

    setImages((prevState) =>
      prevState.map((item) => {
        const itemIdentifier = getImageIdentifier(item)

        return identifier === itemIdentifier
          ? {
              ...item,
              credit: normalizedCredit || undefined
            }
          : item
      })
    )
  }, [])

  /**
   * onSetImageAlternativeText
   *
   * @description Sets alternative text for the selected image identifier.
   * @param {string} identifier - Image identifier.
   * @param {string} alternativeText - Alternative text value.
   * @returns {void}
   *
   */
  const onSetImageAlternativeText = useCallback(
    (identifier: string, alternativeText: string) => {
      setImages((prevState) =>
        prevState.map((item) => {
          const itemIdentifier = getImageIdentifier(item)

          return identifier === itemIdentifier
            ? {
                ...item,
                alternativeText
              }
            : item
        })
      )
    },
    []
  )

  /**
   * onSetImageCaption
   *
   * @description Sets caption text for the selected image identifier.
   * @param {string} identifier - Image identifier.
   * @param {string} caption - Caption value.
   * @returns {void}
   *
   */
  const onSetImageCaption = useCallback(
    (identifier: string, caption: string) => {
      setImages((prevState) =>
        prevState.map((item) => {
          const itemIdentifier = getImageIdentifier(item)

          return identifier === itemIdentifier
            ? {
                ...item,
                caption
              }
            : item
        })
      )
    },
    []
  )

  /**
   * onUpdateSelectedImagePosition
   *
   * @description Update the selected image position by either moving it left or right
   * @param {string} direction - The direction to move the image (left or right)
   * @returns
   *
   */
  const onUpdateSelectedImagePosition = useCallback(
    (direction: "left" | "right") => (identifier: string) => {
      setImages((prevState) => {
        const newState = [...prevState]

        // Find the index of the existing array if it exists
        const index = imagesAndIdentifiers.findIndex(
          (item) => item.identifier === identifier
        )

        if (index < 0) {
          return newState
        }

        // Move the image left or right
        const newIndex = direction === "left" ? index - 1 : index + 1

        if (newIndex < 0 || newIndex >= newState.length) {
          return newState
        }

        // Swap the images
        const temp = newState[index]
        newState[index] = newState[newIndex]
        newState[newIndex] = temp

        return newState
      })
    },
    [imagesAndIdentifiers]
  )

  return {
    ...props,
    imageUrlRefs,
    imagesAndIdentifiers,
    modal,
    onAddImage,
    onConfirm,
    onImageUrlChange,
    onSetImageAlternativeText,
    onSetImageCaption,
    onRegenerateScreenshotMetadataField,
    onSelectImage,
    onSetImageCredit,
    onUpdateSelectedImagePosition
  }
}

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  ImageSelector
)
